import { useState, useEffect, useCallback } from 'react';
import type { User } from 'firebase/auth';
import { EMAIL_LOCAL_STORAGE_KEY } from '../const/local-storage-keys';
import { getMinutes } from '@/utils/helpers/get-minutes';

type FirebaseAuthClient = {
  auth: import('firebase/auth').Auth;
  appleProvider: import('firebase/auth').OAuthProvider;
  googleProvider: import('firebase/auth').GoogleAuthProvider;
  module: typeof import('firebase/auth');
};

let firebaseAuthClientPromise: Promise<FirebaseAuthClient> | null = null;

const getFirebaseAuthClient = async (): Promise<FirebaseAuthClient> => {
  if (!firebaseAuthClientPromise) {
    firebaseAuthClientPromise = (async () => {
      const [firebaseAuthModule, { firebaseApp }] = await Promise.all([
        import('firebase/auth'),
        import('../firebase/firebase-app'),
      ]);

      const auth = firebaseAuthModule.getAuth(firebaseApp);
      const googleProvider = new firebaseAuthModule.GoogleAuthProvider();
      googleProvider.setCustomParameters({ prompt: 'select_account' });
      const appleProvider = new firebaseAuthModule.OAuthProvider('apple.com');
      appleProvider.addScope('email');
      appleProvider.addScope('name');

      return {
        auth,
        appleProvider,
        googleProvider,
        module: firebaseAuthModule,
      };
    })();
  }

  return firebaseAuthClientPromise;
};

interface IUseFirebaseAuthProps {
  autoInit?: boolean;
}

export const useFirebaseAuth = ({ autoInit = false }: IUseFirebaseAuthProps = {}) => {
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [shouldInitAuth, setShouldInitAuth] = useState(false);

  const authStateChanged = useCallback(async (user: User | null) => {
    setAuthUser(user);
    setLoading(false);
  }, []);

  const clear = () => {
    setAuthUser(null);
    setLoading(false);
  };

  const ensureAuthReady = useCallback(async (): Promise<void> => {
    await getFirebaseAuthClient();
    setShouldInitAuth(true);
  }, []);

  const getCurrentUser = useCallback(async (): Promise<User | null> => {
    const { auth } = await getFirebaseAuthClient();
    return auth.currentUser;
  }, []);

  const sendLinkToEmail = async (email: string) => {
    try {
      await ensureAuthReady();
      const { auth, module } = await getFirebaseAuthClient();
      const actionCodeSettings = {
        url: location.href,
        handleCodeInApp: true,
      };

      window.localStorage.setItem(EMAIL_LOCAL_STORAGE_KEY, email);
      await module.sendSignInLinkToEmail(auth, email, actionCodeSettings);
    } catch (error) {
      console.error(error);
    }
  };

  const signInWithEmail = useCallback(async (email: string, href: string) => {
    try {
      await ensureAuthReady();
      const { auth, module } = await getFirebaseAuthClient();
      await module.signInWithEmailLink(auth, email, href);
      clearEmailFromStorage();
    } catch (error) {
      console.error(error);
      throw new Error('Modal reopened');
    }
  }, [ensureAuthReady]);

  const signInWithGoogle = async () => {
    try {
      await ensureAuthReady();
      const { auth, googleProvider, module } = await getFirebaseAuthClient();
      await module.signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error(error);
      throw new Error('Modal reopened');
    }
  };

  const signInWithApple = async () => {
    await ensureAuthReady();
    const { auth, appleProvider, module } = await getFirebaseAuthClient();
    await module.signInWithPopup(auth, appleProvider);
  };

  const signOut = async () => {
    try {
      await ensureAuthReady();
      const { auth, module } = await getFirebaseAuthClient();
      await module.signOut(auth);
      clear();
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    } catch (e) {
      console.error(e);
    }
  };

  function getOriginHref() {
    if (typeof window === 'undefined') {
      return;
    }

    return window.location.href;
  }

  function getStorageEmail() {
    if (typeof window === 'undefined') {
      return;
    }

    return window.localStorage.getItem(EMAIL_LOCAL_STORAGE_KEY);
  }

  function clearEmailFromStorage() {
    window.localStorage.removeItem(EMAIL_LOCAL_STORAGE_KEY);
  }

  useEffect(() => {
    if (!autoInit || typeof window === 'undefined') {
      return;
    }

    const markReady = () => {
      setShouldInitAuth(true);
    };

    if (document.readyState === 'complete') {
      markReady();
      return;
    }

    window.addEventListener('load', markReady, { once: true });
    return () => window.removeEventListener('load', markReady);
  }, [autoInit]);

  useEffect(() => {
    if (!shouldInitAuth) {
      return;
    }

    setLoading(true);

    let unsubscribe: (() => void) | null = null;
    let isMounted = true;

    (async () => {
      const { auth, module } = await getFirebaseAuthClient();
      if (!isMounted) {
        return;
      }
      unsubscribe = module.onAuthStateChanged(auth, authStateChanged);
    })();

    return () => {
      isMounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [authStateChanged, shouldInitAuth]);

  useEffect(() => {
    if (!shouldInitAuth) {
      return;
    }

    const href = getOriginHref();
    (async () => {
      const { auth, module } = await getFirebaseAuthClient();
      if (href && module.isSignInWithEmailLink(auth, href)) {
        const email = getStorageEmail();
        if (email) {
          await signInWithEmail(email, href);
        }
      }
    })();
  }, [signInWithEmail, shouldInitAuth]);

  useEffect(() => {
    if (!shouldInitAuth) {
      return;
    }

    let unsubscribe: (() => void) | null = null;
    let isMounted = true;

    (async () => {
      const { auth } = await getFirebaseAuthClient();
      if (!isMounted) {
        return;
      }
      unsubscribe = auth.onIdTokenChanged(async (user) => {
        setAuthUser(user ?? null);
      });
    })();

    return () => {
      isMounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [shouldInitAuth]);

  useEffect(() => {
    if (!shouldInitAuth) {
      return;
    }

    const handle = setInterval(async () => {
      const { auth } = await getFirebaseAuthClient();
      const user = auth.currentUser;
      if (user) await user.getIdToken(true);
    }, getMinutes(10));

    return () => clearInterval(handle);
  }, [shouldInitAuth]);

  return {
    authUser,
    loading,
    ensureAuthReady,
    getCurrentUser,
    sendLinkToEmail,
    signInWithGoogle,
    signInWithApple,
    signOut,
  };
};
